// ------------------- Script to create the services that call the API --------------
// ----------------------------------------------------------------------------------

// Set up functions to help the calling

// base URL for the API from the env or default to local
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// create a custom error class to handle API errors
class ApiError extends Error { 
  constructor(message, status, data) { 
    super(message); // call the parent constructor with the message
    this.status = status; // store the HTTP status code
    this.data = data; // store any additional data from the response
    this.name = 'ApiError'; // set the error name to 'ApiError'
  }
}

// function to handle the response from the API
const handleResponse = async (response) => {
  if (!response.ok) { // if the response is not ok 
    let errorMessage = `HTTP ${response.status}`; // set a default error message with the HTTP status code

    try { // try to parse the response as JSON to get more details about the error
      const errorData = await response.json(); // parse the response as JSON
      const detail = errorData.detail; // get the details of the error from the response data

      if (Array.isArray(detail)) { // if the detail is an array, it means there are multiple validation errors
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join('; '); // join the error messages into a single string
      } else {
        errorMessage = detail || errorMessage; // use the error message from the response data or the default error message
      }

    } catch { // if parsing the response as JSON fails, use the status text from the response
      errorMessage = response.statusText || errorMessage;
    }

    throw new ApiError(errorMessage, response.status); // throw a new ApiError with the error message and status code
  }
  return response.json();
};

// function to make an API request
const apiRequest = async (endpoint, options = {}) => {

  const url = `${API_BASE_URL}${endpoint}`; // set the url to the base URL plus the endpoint

  const config = { // set up the configuration for the fetch request
    headers: {
      'Content-Type': 'application/json', // set the content type to JSON
      ...options.headers,
    },
    ...options,
  };

  try { // try to make the fetch request
    const response = await fetch(url, config); // make the fetch request to the API
    return await handleResponse(response); // handle the response from the API

  } catch (error) { // if there are any errors during the fetch request, throw a new ApiError with the error message and status code
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(`Network error: ${error.message}`, 0); // throw a new ApiError with the error message and status code 0 for network errors
  }
};

// ----------------------------------------------------------------------------------
// Functions to call the API for stock data

export const getAvailableTickers = async () => {
  return apiRequest('/api/tickers'); // request to call the backend API endpoint for available tickers
};

export const getBatchStockData = async ({ symbols, timeframe = 'MAX' }) => {
  const params = new URLSearchParams({ symbols, timeframe });
  return apiRequest(`/api/stock/batch?${params}`);
};

export const getLatestPrice = async (symbol) => {
  const params = new URLSearchParams({ symbol });
  return apiRequest(`/api/stock/latest?${params}`);
};

export const getOHLVCData = async ({ symbol, timeframe = 'MAX', compare = null }) => {
  const params = new URLSearchParams({ symbol, timeframe });
  if (compare) params.append('compare', compare); // add compare parameter if it is provided
  return apiRequest(`/api/stock/ohlvc?${params}`);
};

// ----------------------------------------------------------------------------------
// Functions to call the API for macroeconomic data
export const getMacroData = async ({ timeframe = 'MAX' } = {}) => {
  const params = new URLSearchParams({ timeframe });
  return apiRequest(`/api/macro/latest?${params}`);
};

export const getMacroHistory = async (indicator, timeframe = 'MAX') => {
  const params = new URLSearchParams({ indicator, timeframe });
  return apiRequest(`/api/macro/history?${params}`);
};

// ----------------------------------------------------------------------------------
// Functions to call the API for news data and sentiment analysis
export const getNewsData = async ({ pageSize = 20, timeframe = 'MAX' } = {}) => {
  const params = new URLSearchParams({ pageSize, timeframe });
  return apiRequest(`/api/news?${params}`);
};

export const getNewsSentiment = async ({ pageSize = 50 } = {}) => {
  const params = new URLSearchParams({ pageSize });
  return apiRequest(`/api/news/sentiment?${params}`);
};
// ----------------------------------------------------------------------------------
// Function to call the API for the trends data
export const getGoogleTrendsData = async ({ timeframe = '3M' } = {}) => {
  const params = new URLSearchParams({ timeframe });
  return apiRequest(`/api/google-trends?${params}`);
};

// ----------------------------------------------------------------------------------
// Functions to call the API for model related endpoints

export const getAvailableModels = async () => {
  return apiRequest('/api/models');
};

export const getModelPredictions = async ({ model = 'xgb', timeframe = '2025', k_long = null, k_short = null, symbols = null, date_from = null, date_to = null } = {}) => {
  const params = new URLSearchParams({ model, timeframe });

  if (k_long  !== null)   params.append('k_long',    k_long);
  if (k_short !== null)   params.append('k_short',   k_short);
  if (symbols !== null)   params.append('symbols',   symbols);
  if (date_from !== null) params.append('date_from', date_from);
  if (date_to   !== null) params.append('date_to',   date_to);

  return apiRequest(`/api/models/predict?${params}`);
};

export const getHoldings = async ({ model, date, split = 'test', year = null, k_long = null, k_short = null } = {}) => {
  const params = new URLSearchParams({ model, date, split });

  if (year !== null) params.append('year', year);
  if (k_long !== null) params.append('k_long', k_long);
  if (k_short !== null) params.append('k_short', k_short);

  return apiRequest(`/api/models/holdings?${params}`);
};

export const getOOFPredictions = async ({ model = 'xgb', year = '2022' } = {}) => {
  const params = new URLSearchParams({ model, year });
  return apiRequest(`/api/models/oof?${params}`);
};

export const getSHAPValues = async ({ model = 'xgb', n_explain = 2000 } = {}) => {
  const params = new URLSearchParams({ model, n_explain });
  return apiRequest(`/api/models/shap?${params}`);
};

// ----------------------------------------------------------------------------------

// AI assistant API functions
export const researchQuery = async ({ question, symbol = null, timeframe = '2024', model = null } = {}) => {
  const params = new URLSearchParams({ question, timeframe });

  if (symbol) params.append('symbol', symbol);
  if (model) params.append('model', model);
  
  return apiRequest(`/api/research/query?${params}`, { method: 'POST' });
};

export const getResearchUsage = async () => {
  return apiRequest('/api/research/usage');
};

// ----------------------------------------------------------------------------------
// Health functions
export const getHealthCheck = async () => {
  return apiRequest('/api/health');
};

export default { // export all the functions to be used in other parts of the application
  getOHLVCData,
  getLatestPrice,
  getBatchStockData,
  getAvailableTickers,
  getMacroData,
  getMacroHistory,
  getNewsData,
  getNewsSentiment,
  getGoogleTrendsData,
  getModelPredictions,
  getOOFPredictions,
  getSHAPValues,
  getAvailableModels,
  getHealthCheck,
};
